import type React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";

function MetaLogo() {
  return (
    <img
      src="/meta-color.svg"
      alt=""
      aria-hidden="true"
      className="h-full w-full"
      style={{
        filter:
          "drop-shadow(0 0 10px rgba(34,211,238,1)) drop-shadow(0 0 20px rgba(59,130,246,0.8))",
      }}
    />
  );
}

export function MadsCssAvatar({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 8, stiffness: 250, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  const rotateX = useTransform(smoothY, [-100, 100], [30, -30]);
  const rotateY = useTransform(smoothX, [-100, 100], [-30, 30]);
  const translateX = useTransform(smoothX, [-100, 100], [-10, 10]);
  const translateY = useTransform(smoothY, [-100, 100], [-10, 10]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    mouseX.set(Math.max(-100, Math.min(100, event.clientX - centerX)));
    mouseY.set(Math.max(-100, Math.min(100, event.clientY - centerY)));
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      className={cn("relative flex h-full w-full items-center justify-center group", className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: 1000, ...style }}
    >
      <motion.div
        className="relative h-full w-full cursor-pointer preserve-3d"
        style={{
          rotateX,
          rotateY,
          x: translateX,
          y: translateY,
          transformStyle: "preserve-3d",
        }}
      >
        <motion.div
          className="relative h-full w-full"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-600 via-zinc-800 to-black shadow-[0_0_15px_rgba(0,0,0,0.8)]">
            <div className="absolute inset-x-0 top-0 h-[45%] rounded-t-2xl bg-gradient-to-b from-white/20 to-transparent opacity-80" />
            <div className="absolute inset-[3px] rounded-[13px] border border-zinc-500/30 bg-zinc-900/90 backdrop-blur-sm" />
            <div className="absolute left-1/2 top-1.5 h-0.5 w-4 -translate-x-1/2 rounded-full bg-zinc-400/60" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 rounded-b-2xl bg-gradient-to-t from-black/90 to-transparent" />
            <div className="absolute bottom-2 left-1/2 h-1 w-6 -translate-x-1/2 rounded-full bg-black shadow-inner" />
            <div className="absolute bottom-1.5 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-zinc-600/50" />
            <div className="absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r-sm bg-zinc-400/80 shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            <div className="absolute right-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-l-sm bg-zinc-400/80 shadow-[0_0_8px_rgba(161,161,170,0.8)]" />
            <div className="absolute -top-1 left-1/2 h-2 w-1 -translate-x-1/2 rounded-t-sm bg-zinc-500" />
            <motion.div
              className="absolute -top-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,1)]"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>

          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            style={{ transform: "translateZ(20px)" }}
          >
            <div className="absolute inset-x-0 top-[30%] h-[40%] rounded-lg bg-blue-500/20 blur-md" />
            <div className="absolute inset-x-1 top-[35%] h-[30%] rounded-lg bg-black/60 blur-[2px]" />
            <motion.div
              className="relative z-10 h-[85%] w-[85%]"
              initial={{ opacity: 0.9 }}
              animate={{ opacity: [0.85, 1, 0.85] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <MetaLogo />
            </motion.div>
          </motion.div>

          <motion.div
            className="absolute inset-x-0 h-[2px] bg-cyan-300/60 blur-[1px] shadow-[0_0_8px_rgba(34,211,238,0.8)]"
            animate={{ top: ["15%", "85%", "15%"] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            style={{ transform: "translateZ(5px)" }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
